<template>
    <div class="wtc-component__loading">
        <div :class="animation">
            <div></div>
            <div></div>
            <div></div>
            <div></div>
            <div></div>
            <div></div>
            <div></div>
            <div></div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { PropType } from "vue";

type LoadingAnimations = "x--load-bars" | "x--load-dots" | "x--load-circle";

defineProps({
    animation: {
        type: String as PropType<LoadingAnimations>,
        default: "x--load-bars"
    }
});
</script>

<style lang="scss">
.wtc-component__loading {
    display: flex;
    justify-content: center;

    + .wtc-component__loading {
        display: none;
    }

    .x--load-bars {
        display: inline-block;
        position: relative;
        width: 40px;
        height: 40px;
        transform: scale(1);

        div {
            display: inline-block;
            position: absolute;
            left: 4px;
            width: 8px;
            background: var(--loading-color);
            animation: x--load-bars 1.2s cubic-bezier(0, 0.5, 0.5, 1) infinite;

            &:nth-child(1) {
                left: 4px;
                animation-delay: -0.24s;
            }

            &:nth-child(2) {
                left: 16px;
                animation-delay: -0.12s;
            }

            &:nth-child(3) {
                left: 28px;
                animation-delay: 0;
            }

            &:nth-child(1n + 4) {
                display: none;
            }
        }

        @keyframes x--load-bars {
            0% {
                top: 4px;
                height: 32px;
            }
            50%,
            100% {
                top: 12px;
                height: 16px;
            }
        }
    }

    .x--load-dots {
        display: inline-block;
        position: relative;
        width: 40px;
        height: 40px;
        transform: scale(1.25);

        div {
            position: absolute;
            top: 17px;
            width: 7px;
            height: 7px;
            border-radius: 50%;
            background: var(--loading-color);
            animation-timing-function: cubic-bezier(0, 1, 1, 0);

            &:nth-child(1) {
                left: 4px;
                animation: x--load-dots-01 0.6s infinite;
            }
            &:nth-child(2) {
                left: 4px;
                animation: x--load-dots-02 0.6s infinite;
            }
            &:nth-child(3) {
                left: 16px;
                animation: x--load-dots-02 0.6s infinite;
            }
            &:nth-child(4) {
                left: 28px;
                animation: x--load-dots-03 0.6s infinite;
            }
            &:nth-child(1n + 5) {
                display: none;
            }
        }

        @keyframes x--load-dots-01 {
            0% {
                transform: scale(0);
            }
            100% {
                transform: scale(1);
            }
        }
        @keyframes x--load-dots-03 {
            0% {
                transform: scale(1);
            }
            100% {
                transform: scale(0);
            }
        }
        @keyframes x--load-dots-02 {
            0% {
                transform: translate(0, 0);
            }
            100% {
                transform: translate(12px, 0);
            }
        }
    }

    .x--load-circle {
        display: inline-block;
        position: relative;
        width: 40px;
        height: 40px;
        transform: scale(1);

        div {
            animation: x--load-circle 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
            transform-origin: 20px 20px;

            &:after {
                content: " ";
                display: block;
                position: absolute;
                width: 4px;
                height: 4px;
                border-radius: 50%;
                background: var(--loading-color);
                margin: -2px 0 0 -2px;
            }

            &:nth-child(1) {
                animation-delay: -0.036s;
            }
            &:nth-child(1):after {
                top: 31px;
                left: 32px;
            }
            &:nth-child(2) {
                animation-delay: -0.072s;
            }
            &:nth-child(2):after {
                top: 34px;
                left: 28px;
            }
            &:nth-child(3) {
                animation-delay: -0.108s;
            }
            &:nth-child(3):after {
                top: 36px;
                left: 24px;
            }
            &:nth-child(4) {
                animation-delay: -0.144s;
            }
            &:nth-child(4):after {
                top: 37px;
                left: 20px;
            }
            &:nth-child(5) {
                animation-delay: -0.18s;
            }
            &:nth-child(5):after {
                top: 36px;
                left: 16px;
            }
            &:nth-child(6) {
                animation-delay: -0.216s;
            }
            &:nth-child(6):after {
                top: 34px;
                left: 12px;
            }
            &:nth-child(7) {
                animation-delay: -0.252s;
            }
            &:nth-child(7):after {
                top: 32px;
                left: 9px;
            }
            &:nth-child(8) {
                animation-delay: -0.288s;
            }
            &:nth-child(8):after {
                top: 28px;
                left: 6px;
            }
        }

        @keyframes x--load-circle {
            0% {
                transform: rotate(0deg);
            }
            100% {
                transform: rotate(360deg);
            }
        }
    }
}
</style>
